import { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { governanceApiClient } from '@/core/infra_api_client'
import type { OperationLogLine } from '@/core/infra_types'
import { getConfig } from '@/core/infra_runtime_env'

const GATEWAY_URL = getConfig('VITE_API_URL', 'http://localhost:8090') || '/api'

export type HostLogConnectionState = 'idle' | 'streaming' | 'error'

// The services an agent-host that predates GET .../logs/services streams from
// log files. The screen uses this list only when the host does not serve its
// own (agion/Agion#3438).
export const HOST_LOG_FALLBACK_SERVICES = [
  'agent-host',
  'mission-runner',
  'agion-mcp',
  'channel-gateway',
  'intelligence-runtime',
  'intelligence-ingestion-worker',
  'intelligence-zone',
  'intelligence-ingestion',
  'agion-alloy',
] as const

export type HostLogService = string

/** Where agent-host reads a service's lines: a log file or a systemd journal. */
export type HostLogSource = 'file' | 'journal'

export interface HostLogServiceEntry {
  id: HostLogService
  source: HostLogSource
}

const FALLBACK_SERVICE_ENTRIES: HostLogServiceEntry[] =
  HOST_LOG_FALLBACK_SERVICES.map(id => ({ id, source: 'file' }))

/**
 * The services this host streams, as its agent-host lists them. An older host
 * answers 404; it and any other failure fall back to the file services every
 * agent-host has, with no journal tab.
 */
export function useHostLogServices(hostId: string): { services: HostLogServiceEntry[], isLoading: boolean } {
  const { data, isLoading } = useQuery({
    queryKey: ['agent-host', hostId, 'log-services'],
    queryFn: () => governanceApiClient.get<{ services: HostLogServiceEntry[] }>(`agent-hosts/${hostId}/logs/services`),
    enabled: !!hostId,
    retry: false,
    staleTime: Infinity,
  })
  const services = data?.services.length ? data.services : FALLBACK_SERVICE_ENTRIES
  return { services, isLoading }
}

const HOST_LOG_SERVICE_ALIASES: Record<string, HostLogService> = {
  'agent-runtime': 'mission-runner',
  'agion-runtime': 'mission-runner',
  'agion-mission-runner': 'mission-runner',
  'agion-agent-runtime': 'mission-runner',
}

export function canonicalHostLogService(service: string | null | undefined): string | null {
  const normalized = service?.trim().toLowerCase()
  if (!normalized) return null
  return HOST_LOG_SERVICE_ALIASES[normalized] ?? normalized
}

export function normalizeHostLogServices(services: readonly string[]): string[] {
  const out: string[] = []
  const seen = new Set<string>()
  for (const service of services) {
    const canonical = canonicalHostLogService(service)
    if (!canonical || seen.has(canonical)) continue
    seen.add(canonical)
    out.push(canonical)
  }
  return out
}

// The backend `tail -n N file1 file2 ...` emits all historical lines of file1
// before any of file2, so a single 30s SSE window dumps up to ~lines×2 events
// per service in URL order. With a flat global cap, the service listed first
// (agent-host) was completely evicted by later, chattier services like
// intelligence-zone. Cap per unit instead — each service keeps its own slot.
//
// 400 covers the worst case (lines=200 historical replay × {main, error} files)
// so the initial replay is never truncated by buffer pressure.
const PER_UNIT_LIMIT = 400

// A native EventSource reconnects by itself, forever, roughly every 3s, and
// fires onerror on every attempt. Setting error state without calling close()
// therefore leaves the browser retrying for as long as the tab is open: one
// backgrounded log tab against an offline host is an open-ended request stream.
//
// Each attempt opens an SSE body that APISIX bills as `type="request"` latency
// when it closes, so the retries also landed in the gateway's P99 and paged
// APISIXLatencyP99High on the shared governance/infra routes.
//
// So close the stream on error and drive reconnection here instead, bounded and
// backed off: 1s, 2s, 4s, 8s, 16s, then stop and let the user retry by hand.
const MAX_RECONNECT_ATTEMPTS = 5
const RECONNECT_BASE_DELAY_MS = 1000
const RECONNECT_MAX_DELAY_MS = 30000

// Reaching `open` is too weak a signal to refund the retry budget: this proxy
// accepts the connection before it knows the host is gone, so a dead host still
// produces open -> drop -> open -> drop. Refunding on open would reset the
// counter every cycle and retry at the base delay forever -- a tighter loop
// than the browser default this replaces. Require the stream to have actually
// held for a while before treating it as healthy.
const RECONNECT_STABLE_MS = 30000

export function reconnectDelayMs(attempt: number): number {
  return Math.min(RECONNECT_BASE_DELAY_MS * 2 ** (attempt - 1), RECONNECT_MAX_DELAY_MS)
}

export const HOST_LOG_ERROR_RECONNECTING = 'Connection lost. Host may be offline.'
export const HOST_LOG_ERROR_EXHAUSTED =
  `Connection lost. Gave up after ${MAX_RECONNECT_ATTEMPTS} reconnect attempts — use Reconnect to try again.`
export const HOST_LOG_ERROR_NO_LOG_FILES = 'No log files for this service on this host.'
export const HOST_LOG_ERROR_KUBERNETES_HOST = 'Log streaming is not available on Kubernetes hosts.'
export const HOST_LOG_ERROR_HOST_UNREACHABLE = 'Host not reachable over VPN.'
export const HOST_LOG_ERROR_HOST_NOT_FOUND = 'Host not found.'

/** Why the stream is not showing logs; the viewer translates it. */
export type HostLogErrorKind =
  | 'reconnecting'
  | 'exhausted'
  | 'noLogFiles'
  | 'kubernetesHost'
  | 'hostUnreachable'
  | 'hostNotFound'

export const HOST_LOG_ERROR_MESSAGES: Record<HostLogErrorKind, string> = {
  reconnecting: HOST_LOG_ERROR_RECONNECTING,
  exhausted: HOST_LOG_ERROR_EXHAUSTED,
  noLogFiles: HOST_LOG_ERROR_NO_LOG_FILES,
  kubernetesHost: HOST_LOG_ERROR_KUBERNETES_HOST,
  hostUnreachable: HOST_LOG_ERROR_HOST_UNREACHABLE,
  hostNotFound: HOST_LOG_ERROR_HOST_NOT_FOUND,
}

export const HOST_LOG_MAX_RECONNECT_ATTEMPTS = MAX_RECONNECT_ATTEMPTS

// EventSource hides the HTTP status, so every refusal looked like a dropped
// connection. agent-host has no log file for the requested services (404) or
// runs in Kubernetes substrate mode (405): retrying gets the same answer, so
// the hook stops and the user retries with Reconnect. Governance cannot reach
// the host over its VPN (502): the VPN can come back, so the bounded retry
// goes on and the message names the VPN while it does. Governance answers 404
// itself when it has no such host; its body carries no reason code, so the
// text it sets (host_proxy_target.go) tells it apart from agent-host's 404.
const STATUS_ERROR_KINDS: Record<number, HostLogErrorKind> = {
  404: 'noLogFiles',
  405: 'kubernetesHost',
  502: 'hostUnreachable',
}

const TERMINAL_ERROR_KINDS: ReadonlySet<HostLogErrorKind> = new Set(['noLogFiles', 'kubernetesHost', 'hostNotFound'])

const GOVERNANCE_HOST_NOT_FOUND = 'Host or group not found'

export interface HostLogStreamAnswer {
  status: number
  /** Read for a 404 only; any other body may be the open-ended stream. */
  body: string
}

/**
 * Reads the HTTP answer of the stream URL with one plain GET, with the same
 * cookie credentials the EventSource sends, and aborts the body at once. It
 * returns null when the request itself fails (a real disconnect).
 */
export async function readHostLogStreamAnswer(url: string, signal: AbortSignal): Promise<HostLogStreamAnswer | null> {
  const controller = new AbortController()
  const abort = () => controller.abort()
  signal.addEventListener('abort', abort, { once: true })
  try {
    const response = await fetch(url, {
      credentials: 'include',
      headers: { Accept: 'text/event-stream' },
      signal: controller.signal,
    })
    const body = response.status === 404 ? await response.text() : ''
    return { status: response.status, body }
  } catch {
    return null
  } finally {
    signal.removeEventListener('abort', abort)
    controller.abort()
  }
}

export function hostLogErrorKindForAnswer(answer: HostLogStreamAnswer | null): HostLogErrorKind | null {
  if (answer === null) return null
  if (answer.status === 404 && answer.body.includes(GOVERNANCE_HOST_NOT_FOUND)) return 'hostNotFound'
  return STATUS_ERROR_KINDS[answer.status] ?? null
}

export interface HostLogLine extends OperationLogLine {
  unit?: string
}

const unitKey = (line: HostLogLine): string => line.unit ?? ''

export function appendWithPerUnitCap(
  prev: HostLogLine[],
  next: HostLogLine,
  perUnitLimit: number = PER_UNIT_LIMIT,
): HostLogLine[] {
  const key = unitKey(next)
  let count = 0
  for (const l of prev) {
    if (unitKey(l) === key) count++
  }
  if (count < perUnitLimit) {
    return [...prev, next]
  }
  for (let i = 0; i < prev.length; i++) {
    if (unitKey(prev[i]!) === key) {
      const out = prev.slice()
      out.splice(i, 1)
      out.push(next)
      return out
    }
  }
  return [...prev, next]
}

interface UseHostLogsOptions {
  hostId: string
  services?: string[]
  lines?: number
  errorsOnly?: boolean
  enabled?: boolean
}

interface UseHostLogsResult {
  logs: HostLogLine[]
  connectionState: HostLogConnectionState
  /** English text of errorKind. */
  error: string | null
  errorKind: HostLogErrorKind | null
  reconnect: () => void
  clearLogs: () => void
}

/**
 * SSE hook for live BYOH host log streaming.
 * Connects to /v1/governance/agent-hosts/{hostId}/logs/stream via VPN proxy.
 * Stream is open-ended (no 'done' event) — ends when user disconnects or host goes offline.
 */
export function useHostLogs({
  hostId,
  services,
  lines = 200,
  errorsOnly = false,
  enabled = true,
}: UseHostLogsOptions): UseHostLogsResult {
  const [logs, setLogs] = useState<HostLogLine[]>([])
  const [isConnected, setIsConnected] = useState(false)
  const [errorKind, setErrorKind] = useState<HostLogErrorKind | null>(null)
  const eventSourceRef = useRef<EventSource | null>(null)
  // The status read after an error; aborted when a newer connection starts or
  // the hook unmounts, so a stale answer never overwrites a newer state.
  const statusProbeRef = useRef<AbortController | null>(null)
  const reconnectAttemptsRef = useRef(0)
  const openedAtRef = useRef<number | null>(null)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // `connect` is rebuilt whenever the URL changes; a scheduled retry must call
  // the current one, not the closure captured when the timer was armed.
  const connectRef = useRef<() => void>(() => {})

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current !== null) {
      clearTimeout(reconnectTimerRef.current)
      reconnectTimerRef.current = null
    }
  }, [])

  const abortStatusProbe = useCallback(() => {
    statusProbeRef.current?.abort()
    statusProbeRef.current = null
  }, [])

  const url = useMemo(() => {
    const params = new URLSearchParams()
    normalizeHostLogServices(services ?? HOST_LOG_FALLBACK_SERVICES).forEach(s => params.append('service', s))
    params.set('lines', String(lines))
    if (errorsOnly) params.set('errors_only', 'true')
    return `${GATEWAY_URL}/v1/governance/agent-hosts/${hostId}/logs/stream?${params.toString()}`
  }, [hostId, services, lines, errorsOnly])

  const connect = useCallback(() => {
    if (!hostId || !enabled) return

    clearReconnectTimer()
    abortStatusProbe()

    if (eventSourceRef.current) {
      eventSourceRef.current.close()
    }

    const eventSource = new EventSource(url, { withCredentials: true })
    eventSourceRef.current = eventSource

    eventSource.onopen = () => {
      openedAtRef.current = Date.now()
      setIsConnected(true)
      setErrorKind(null)
    }

    eventSource.onmessage = (event) => {
      try {
        const logLine = JSON.parse(event.data as string) as HostLogLine
        const unit = canonicalHostLogService(logLine.unit)
        setLogs(prev => appendWithPerUnitCap(prev, unit ? { ...logLine, unit } : logLine))
      } catch {
        setLogs(prev => appendWithPerUnitCap(prev, {
          timestamp: new Date().toISOString(),
          level: 'info' as const,
          message: event.data as string,
        }))
      }
    }

    eventSource.onerror = () => {
      // Stop the browser's own endless retry before deciding anything: without
      // this close() the loop continues underneath us no matter what we set.
      eventSource.close()
      if (eventSourceRef.current === eventSource) {
        eventSourceRef.current = null
      }

      setIsConnected(false)

      // Only a stream that held long enough to look healthy refunds the budget,
      // so an hours-long session is not eventually starved of retries while a
      // flapping one still converges on giving up.
      const openedAt = openedAtRef.current
      openedAtRef.current = null
      if (openedAt !== null && Date.now() - openedAt >= RECONNECT_STABLE_MS) {
        reconnectAttemptsRef.current = 0
      }

      // Read the status the EventSource hid. A terminal refusal replaces the
      // reconnect below. While a reconnect is pending, the message names the
      // VPN for a 502 and a real disconnect otherwise; after the budget is
      // spent it stays "gave up".
      abortStatusProbe()
      const probe = new AbortController()
      statusProbeRef.current = probe
      void readHostLogStreamAnswer(url, probe.signal).then(answer => {
        if (probe.signal.aborted) return
        statusProbeRef.current = null
        const kind = hostLogErrorKindForAnswer(answer)
        if (kind && TERMINAL_ERROR_KINDS.has(kind)) {
          clearReconnectTimer()
          setErrorKind(kind)
          return
        }
        if (reconnectTimerRef.current === null) return
        setErrorKind(kind ?? 'reconnecting')
      })

      if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
        setErrorKind('exhausted')
        return
      }

      reconnectAttemptsRef.current += 1
      // Keep the VPN reason through the retry until the next status read
      // says otherwise, so the message does not flicker between the two.
      setErrorKind(prev => (prev === 'hostUnreachable' ? prev : 'reconnecting'))
      clearReconnectTimer()
      reconnectTimerRef.current = setTimeout(() => {
        reconnectTimerRef.current = null
        connectRef.current()
      }, reconnectDelayMs(reconnectAttemptsRef.current))
    }
  }, [hostId, enabled, url, clearReconnectTimer, abortStatusProbe])

  // Assigned in an effect, not during render: React may discard a render pass,
  // and this ref is read later by an already-armed timer.
  useEffect(() => {
    connectRef.current = connect
  }, [connect])

  useEffect(() => {
    connect()
    return () => {
      clearReconnectTimer()
      abortStatusProbe()
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
        eventSourceRef.current = null
      }
    }
  }, [connect, clearReconnectTimer, abortStatusProbe])

  const reconnect = useCallback(() => {
    setLogs([])
    setErrorKind(null)
    reconnectAttemptsRef.current = 0
    openedAtRef.current = null
    connect()
  }, [connect])

  const clearLogs = useCallback(() => {
    setLogs([])
  }, [])

  const error = errorKind ? HOST_LOG_ERROR_MESSAGES[errorKind] : null
  const connectionState: HostLogConnectionState =
    error ? 'error' :
    isConnected ? 'streaming' :
    'idle'

  return { logs, connectionState, error, errorKind, reconnect, clearLogs }
}
